import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAdvertiser } from "./useAdvertiser";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";

export default function LoginPage() {
  useSeo({ title: "Advertiser sign in | LUX", ...PRIVATE_SEO });
  const navigate = useNavigate();
  const { refetchSession } = useAdvertiser();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      toast.success("Password accepted. Enter your authenticator code.");
      await refetchSession();
      navigate(data.next === "2fa_setup" ? "/advertiser/2fa?mode=setup" : "/advertiser/2fa?mode=login", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="lux-card p-6 sm:p-8" data-testid="login-page">
      <p className="lux-label">Advertiser area</p>
      <h1 className="mt-3 font-serif text-3xl text-lux-text">Sign in</h1>
      <p className="mt-2 text-sm text-lux-muted">You will be asked for your authenticator code after your password.</p>
      <form onSubmit={submit} className="mt-6 space-y-5" noValidate>
        <div>
          <label htmlFor="login-email" className="lux-label">Email</label>
          <input id="login-email" type="email" required autoComplete="email" value={email}
            onChange={(e) => setEmail(e.target.value)} className="lux-input mt-2"
            placeholder="name@example.com" data-testid="login-email-input" />
        </div>
        <div>
          <label htmlFor="login-pw" className="lux-label">Password</label>
          <div className="relative mt-2">
            <input id="login-pw" type={showPw ? "text" : "password"} required autoComplete="current-password" value={password}
              onChange={(e) => setPassword(e.target.value)} className="lux-input pr-11" placeholder="Your password"
              data-testid="login-password-input" />
            <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-2 text-lux-silver hover:text-lux-silverBright"
              data-testid="login-password-toggle">
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {error && <p role="alert" className="text-sm text-lux-error" data-testid="login-error">{error}</p>}
        <button type="submit" disabled={busy} className="lux-btn lux-btn-primary w-full" data-testid="login-submit-button">
          {busy ? "Signing in..." : "Continue"}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-lux-muted">
        New to LUX? <Link to="/advertiser/register" className="lux-link" data-testid="login-register-link">Create an account</Link>
      </p>
    </section>
  );
}
